import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, CloudUpload, KeyRound, Plug, Plus, Tag, Trash2, TriangleAlert } from 'lucide-react';

import { api } from '../lib/api.ts';
import { data as formatarData } from '../lib/formato.ts';
import { useAuth } from '../contexto/auth.tsx';
import { Aviso, Botao, Campo, Carregando, Cartao, Etiqueta, Selecao } from '../componentes/ui.tsx';

interface Entrada {
  id: string;
  versao: string;
  titulo: string;
  descricao: string | null;
  tipo: string;
  data: string;
  enviadoEm: string | null;
  envioErro: string | null;
  criadoPor: string | null;
}

interface Resposta {
  entradas: Entrada[];
  hub: {
    token: string | null;
    configurado: boolean;
    atualizadoEm: string | null;
    testadoEm: string | null;
    testeOk: boolean | null;
    testeDetalhe: string | null;
  };
}

const TIPOS = ['novidade', 'melhoria', 'correcao'];

/** O enum do Hub e sem acento; o que a pessoa le, nao precisa ser. */
const ROTULO_DO_TIPO: Record<string, string> = {
  novidade: 'novidade',
  melhoria: 'melhoria',
  correcao: 'correção',
};

const TOM_DO_TIPO: Record<string, 'bom' | 'medio' | 'neutro'> = {
  novidade: 'bom',
  melhoria: 'neutro',
  correcao: 'medio',
};

/**
 * Novidades e versões.
 *
 * O registro vale por si — é a memória do que mudou no painel. O envio ao Hub
 * é espelho: falhar não desfaz nada aqui, e reenviar é seguro, porque lá a
 * mesma versão atualiza em vez de duplicar.
 */
export function Novidades() {
  const cache = useQueryClient();
  const { pode } = useAuth();
  const podeEditar = pode('changelog', 'editar');
  const podeExcluir = pode('changelog', 'excluir');

  const [criando, setCriando] = useState(false);

  const consulta = useQuery({
    queryKey: ['changelog'],
    queryFn: () => api<Resposta>('/changelog'),
  });

  const enviar = useMutation({
    mutationFn: (id: string) => api<{ ok: true }>(`/changelog/${id}/enviar`, { metodo: 'POST' }),
    onSettled: () => cache.invalidateQueries({ queryKey: ['changelog'] }),
  });

  const excluir = useMutation({
    mutationFn: (id: string) => api<{ ok: true }>(`/changelog/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => cache.invalidateQueries({ queryKey: ['changelog'] }),
  });

  if (consulta.isLoading) return <Carregando texto="Carregando as versões…" />;
  if (consulta.isError) {
    return (
      <Aviso tom="erro" titulo="Não foi possível carregar">
        <p>{(consulta.error as Error).message}</p>
      </Aviso>
    );
  }

  const dados = consulta.data;
  if (dados === undefined) return null;

  const pendentes = dados.entradas.filter((e) => e.enviadoEm === null);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
          <Tag className="size-6 text-slate-400" />
          Novidades e versões
        </h1>
        <p className="mt-1 max-w-prose text-sm text-slate-600">
          O que mudou no painel a cada versão. O registro vale por si; o envio ao Hub de projetos é
          um espelho, e pode ser refeito a qualquer momento sem duplicar nada.
        </p>
      </div>

      <IntegracaoHub hub={dados.hub} podeEditar={podeEditar} />

      <Cartao
        titulo="Versões"
        descricao={
          pendentes.length === 0
            ? 'Todas as versões registradas já foram ao Hub.'
            : `${pendentes.length} ${pendentes.length === 1 ? 'versão ainda não foi' : 'versões ainda não foram'} ao Hub.`
        }
        acoes={
          podeEditar && (
            <Botao onClick={() => setCriando((c) => !c)}>
              <Plus className="size-4" />
              Nova versão
            </Botao>
          )
        }
      >
        {criando && (
          <NovaVersao
            aoSalvar={() => {
              setCriando(false);
              cache.invalidateQueries({ queryKey: ['changelog'] });
            }}
            aoCancelar={() => setCriando(false)}
          />
        )}

        {enviar.isError && (
          <Aviso tom="erro" titulo="O envio falhou">
            <p>{(enviar.error as Error).message}</p>
          </Aviso>
        )}

        {dados.entradas.length === 0 ? (
          <p className="text-sm text-slate-500">
            Nenhuma versão registrada ainda. A primeira entrada costuma ser a versão que está no ar
            hoje.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {dados.entradas.map((e) => (
              <li key={e.id} className="flex flex-wrap items-start gap-3 py-3">
                <span className="w-20 shrink-0 font-mono text-sm font-medium text-slate-900">
                  {e.versao}
                </span>

                <div className="min-w-64 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-900">
                    {e.titulo}
                    <Etiqueta tom={TOM_DO_TIPO[e.tipo] ?? 'neutro'}>
                      {ROTULO_DO_TIPO[e.tipo] ?? e.tipo}
                    </Etiqueta>
                  </p>
                  {e.descricao !== null && (
                    <p className="mt-0.5 max-w-prose text-sm text-slate-600">{e.descricao}</p>
                  )}
                  <p className="mt-1 text-xs text-slate-500">
                    {formatarData(e.data)}
                    {e.criadoPor === null ? '' : ` · ${e.criadoPor}`}
                  </p>
                  {e.envioErro !== null && (
                    <p className="mt-1 flex items-start gap-1 text-xs text-red-700">
                      <TriangleAlert className="mt-0.5 size-3 shrink-0" />
                      {e.envioErro}
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {e.enviadoEm === null ? (
                    <Etiqueta tom="neutro">não enviada</Etiqueta>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                      <Check className="size-3.5" />
                      no Hub
                    </span>
                  )}

                  {podeEditar && (
                    <Botao
                      tipo="secundario"
                      onClick={() => enviar.mutate(e.id)}
                      carregando={enviar.isPending && enviar.variables === e.id}
                      desabilitado={!dados.hub.configurado}
                    >
                      <CloudUpload className="size-4" />
                      {e.enviadoEm === null ? 'Enviar' : 'Reenviar'}
                    </Botao>
                  )}

                  {podeExcluir && (
                    <button
                      onClick={() => {
                        if (window.confirm(`Apagar a versão ${e.versao} daqui?`)) {
                          excluir.mutate(e.id);
                        }
                      }}
                      title="Apagar esta entrada. O que já foi ao Hub continua lá."
                      className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-red-700"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Cartao>
    </div>
  );
}

/** A chave do Hub: colar, testar, e saber qual está em uso. */
function IntegracaoHub({
  hub,
  podeEditar,
}: {
  hub: Resposta['hub'];
  podeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [token, setToken] = useState('');
  const [trocando, setTrocando] = useState(false);

  const salvar = useMutation({
    mutationFn: () => api<{ ok: true }>('/changelog/hub/token', { metodo: 'PUT', corpo: { token } }),
    onSuccess: () => {
      setToken('');
      setTrocando(false);
      cache.invalidateQueries({ queryKey: ['changelog'] });
    },
  });

  const testar = useMutation({
    mutationFn: () => api<{ ok: boolean; detalhe: string }>('/changelog/hub/testar', { metodo: 'POST' }),
    onSettled: () => cache.invalidateQueries({ queryKey: ['changelog'] }),
  });

  return (
    <Cartao
      titulo="Integração com o Hub de projetos"
      descricao="A chave é gerada no Hub e colada aqui uma vez. Ela só escreve novidade deste projeto."
    >
      <div className="space-y-4">
        {hub.configurado && !trocando ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-sm text-slate-700">
              <KeyRound className="size-4 text-slate-400" />
              {hub.token}
            </span>
            {podeEditar && (
              <>
                <Botao
                  tipo="secundario"
                  onClick={() => testar.mutate()}
                  carregando={testar.isPending}
                >
                  <Plug className="size-4" />
                  Testar conexão
                </Botao>
                <Botao tipo="secundario" onClick={() => setTrocando(true)}>
                  Trocar chave
                </Botao>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <Campo
              rotulo="Token do Hub"
              valor={token}
              onChange={setToken}
              desabilitado={!podeEditar}
              dica="Copie do Hub no momento em que ele é gerado — lá o valor não é mostrado de novo."
            />
            <div className="flex gap-3">
              <Botao
                onClick={() => salvar.mutate()}
                carregando={salvar.isPending}
                desabilitado={!podeEditar || token.trim() === ''}
              >
                Salvar chave
              </Botao>
              {trocando && (
                <Botao tipo="secundario" onClick={() => setTrocando(false)}>
                  Cancelar
                </Botao>
              )}
            </div>
          </div>
        )}

        {salvar.isError && (
          <Aviso tom="erro">
            <p>{(salvar.error as Error).message}</p>
          </Aviso>
        )}

        {hub.testadoEm !== null && (
          <Aviso tom={hub.testeOk === true ? 'info' : 'erro'}>
            <p>{hub.testeDetalhe}</p>
          </Aviso>
        )}

        {!hub.configurado && (
          <p className="max-w-prose text-xs text-slate-500">
            Sem a chave, as versões continuam sendo registradas aqui normalmente — só não vão para
            o Hub. Dá para configurar depois e enviar as pendentes de uma vez.
          </p>
        )}
      </div>
    </Cartao>
  );
}

function NovaVersao({ aoSalvar, aoCancelar }: { aoSalvar: () => void; aoCancelar: () => void }) {
  const hoje = new Date();
  const iso = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`;

  const [versao, setVersao] = useState('');
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [tipo, setTipo] = useState('novidade');
  const [data, setData] = useState(iso);

  const salvar = useMutation({
    mutationFn: () =>
      api<{ versao: string }>('/changelog', {
        metodo: 'PUT',
        corpo: { versao, titulo, descricao, tipo, data },
      }),
    onSuccess: aoSalvar,
  });

  return (
    <div className="mb-4 space-y-4 rounded border border-slate-200 bg-slate-50 p-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <Campo
          rotulo="Versão"
          valor={versao}
          onChange={setVersao}
          dica="Registrar a mesma versão de novo corrige a entrada, aqui e no Hub."
        />
        <Selecao
          rotulo="Tipo"
          valor={tipo}
          opcoes={TIPOS}
          permiteVazio={false}
          rotuloDaOpcao={(v) => ROTULO_DO_TIPO[v] ?? v}
          onChange={setTipo}
        />
        <Campo rotulo="Data" tipo="date" valor={data} onChange={setData} />
      </div>

      <Campo
        rotulo="Título"
        valor={titulo}
        onChange={setTitulo}
        dica="É o que a pessoa lê na lista. Até 160 caracteres."
      />
      <Campo
        rotulo="Descrição (opcional)"
        valor={descricao}
        onChange={setDescricao}
        dica="O que mudou, em uma ou duas frases."
      />

      {salvar.isError && (
        <Aviso tom="erro">
          <p>{(salvar.error as Error).message}</p>
        </Aviso>
      )}

      <div className="flex gap-3">
        <Botao
          onClick={() => salvar.mutate()}
          carregando={salvar.isPending}
          desabilitado={versao.trim() === '' || titulo.trim() === ''}
        >
          Salvar versão
        </Botao>
        <Botao tipo="secundario" onClick={aoCancelar}>
          Cancelar
        </Botao>
      </div>
    </div>
  );
}
